// src/store/errorToastMiddleware.ts
import { isRejectedWithValue, type Dispatch, type Middleware } from '@reduxjs/toolkit'
import { toast } from 'sonner'
import { messagesNow } from '@/i18n'
import { registerRateLimitHit } from '@/features/rate-limit/rateLimitBanTracker'
import { setRateLimitBanned } from '@/features/rate-limit/rateLimitSlice'
import { REVIEW_RATE_LIMITED } from '@/features/moderation/lib/parseModerationError'
import { REVIEW_IN_PROGRESS, reviewStateTag } from '@/features/moderation/lib/reviewInProgress'
import { requestIdFromMeta, showRequestIdToast } from '@/lib/requestId'
import { api } from './api'
import { errorText, isSpecificErrorText } from './errorTexts'
import { SILENT_ENDPOINTS } from './silentEndpoints'
import { messages } from './messages'

interface ErrorPayload {
  status?: number | string
  data?: { detail?: unknown; error?: unknown }
}

const detailCode = (detail: unknown): unknown =>
  detail && typeof detail === 'object' ? (detail as { code?: unknown }).code : undefined

// Код отказа: у своих исключений api — `detail.code`, у части ручек — `{error: '...'}`
// (`409 already_fresh` перевода статьи).
const errorCode = (data: ErrorPayload['data']): string | null => {
  const code = detailCode(data?.detail) ?? data?.error
  return typeof code === 'string' ? code : null
}

const isNetworkError = (status: unknown): boolean =>
  status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR'

// Сеть тостится только у мутаций: упавшее чтение экран показывает сам (пустое состояние,
// повтор), а у записи без тоста автор не узнает, что правка не ушла.
export const getErrorMessage = (
  payload: ErrorPayload,
  endpointName = '',
  isMutation = false,
): string | null => {
  const status = payload?.status
  if (isNetworkError(status)) return isMutation ? messagesNow(messages).network : null
  if (typeof status !== 'number' || status === 401) return null // 401 — baseQueryWithReauth
  return errorText(status, errorCode(payload.data), endpointName, payload.data?.detail)
}

// 429: тост не показываем. Одиночный 429 — троттлинг ручки; 429 от разных
// эндпоинтов в окне — бан аккаунта anti-bot системой → полноэкранная заглушка.
const trackRateLimitBan = (dispatch: Dispatch, endpointName?: string) => {
  if (endpointName && registerRateLimitHit(endpointName, Date.now()))
    dispatch(setRateLimitBanned(true))
}

interface RejectedArg {
  endpointName?: string
  type?: 'query' | 'mutation' | 'infinitequery'
  originalArgs?: { meta?: { skipErrorToast?: boolean } }
}

// Возвращает true, если 429 обработан целиком (тоста нет никогда).
const handle429 = (dispatch: Dispatch, payload: ErrorPayload, endpointName?: string): boolean => {
  if (payload?.status !== 429) return false
  // Бизнес-отказ антиспама проверки: ручек две (смена видимости и опрос
  // состояния), и два таких отказа подряд иначе выглядят как бан аккаунта.
  if (detailCode(payload.data?.detail) !== REVIEW_RATE_LIMITED)
    trackRateLimitBan(dispatch, endpointName)
  return true
}

const isToastMuted = (arg?: RejectedArg): boolean =>
  (!!arg?.endpointName && SILENT_ENDPOINTS.has(arg.endpointName)) ||
  !!arg?.originalArgs?.meta?.skipErrorToast

// ID запроса рядом с общим текстом: по нему поддержка достаёт запрос из логов. У точного
// текста (код, валидация) ID не нужен; без заголовка (500 от внешнего слоя) — как раньше.
// Только ответы academy api: `X-Request-Id` другого сервиса (union, прогресс плеера) в логах
// academy не найдётся.
const requestIdToShow = (
  payload: ErrorPayload,
  baseQueryMeta: unknown,
  actionType: string,
): string | null => {
  if (!actionType.startsWith(`${api.reducerPath}/`)) return null
  const id = requestIdFromMeta(baseQueryMeta)
  const status = payload?.status
  if (!id || typeof status !== 'number') return null
  if (isSpecificErrorText(status, errorCode(payload.data), payload.data?.detail)) return null
  return id
}

const handleRejected = (
  dispatch: Dispatch,
  payload: ErrorPayload,
  arg?: RejectedArg,
  baseQueryMeta?: unknown,
  actionType = '',
): void => {
  if (handle429(dispatch, payload, arg?.endpointName)) return
  // Запись во время проверки: состояние проверки на экране устарело — независимо от тоста.
  if (payload?.status === 409 && detailCode(payload.data?.detail) === REVIEW_IN_PROGRESS)
    dispatch(api.util.invalidateTags([reviewStateTag(arg?.endpointName)]))
  if (isToastMuted(arg)) return
  const message = getErrorMessage(payload, arg?.endpointName, arg?.type === 'mutation')
  if (!message) return
  // При обрыве сети падают сразу несколько запросов: тост «нет соединения» не копится.
  if (isNetworkError(payload?.status)) toast.error(message, { id: 'network-error' })
  else {
    const id = requestIdToShow(payload, baseQueryMeta, actionType)
    if (id) showRequestIdToast(message, id)
    else toast.error(message)
  }
}

export const errorToastMiddleware: Middleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (isRejectedWithValue(action))
      handleRejected(
        dispatch,
        action.payload as ErrorPayload,
        (action.meta as { arg?: RejectedArg })?.arg,
        (action.meta as { baseQueryMeta?: unknown })?.baseQueryMeta,
        action.type,
      )
    return next(action)
  }
